(function (root) {
  'use strict';

  const STORAGE_KEY = 'heir.edit.v1';
  const LIMIT = 100;
  const LOOKS = Object.freeze({
    city: Object.freeze({ image: 'campaign-editorial.webp', ids: ['13-core-hoodie', '15-signature-tee', '17-core-sweatpants', '16-classic-cap'] }),
    departure: Object.freeze({ image: 'series-avator-jackets.webp', ids: ['23-avator-jackets-flight-bomber', '35-avator-minimal-logo-tee', '22-cargo-pants', '57-avator-cap-black'] }),
    after_hours: Object.freeze({ image: 'series-graphic.webp', ids: ['74-graphic-hoodie-black', '70-graphic-path', '17-core-sweatpants', '16-classic-cap'] })
  });

  function allowedCatalogue(input) {
    const seen = new Set();
    return (Array.isArray(input) ? input : []).filter((product) => {
      if (!product || typeof product !== 'object' || product.archiveOnly === true) return false;
      if (!/^[a-z0-9][a-z0-9-]{0,99}$/.test(product.id || '') || seen.has(product.id)) return false;
      if (!/^[a-z0-9][a-z0-9._-]*\.(?:webp|png|jpe?g)$/i.test(product.image || '')) return false;
      seen.add(product.id);
      return true;
    });
  }

  function resolveDesign(input, catalogue) {
    if (typeof input !== 'string') return null;
    return allowedCatalogue(catalogue).find((product) => product.id === input || product.handle === input) || null;
  }

  function normalizeItems(input, catalogue) {
    if (!Array.isArray(input)) return [];
    const keys = new Set();
    const result = [];
    for (const item of input.slice(0, 1000)) {
      if (!item || typeof item !== 'object') continue;
      const id = item.kind === 'design' ? resolveDesign(item.id, catalogue)?.id
        : item.kind === 'look' && Object.hasOwn(LOOKS, item.id) ? item.id : null;
      if (!id || keys.has(`${item.kind}:${id}`)) continue;
      keys.add(`${item.kind}:${id}`);
      result.push({ kind: item.kind, id });
      if (result.length === LIMIT) break;
    }
    return result;
  }

  function decodeState(raw, catalogue) {
    try {
      const data = JSON.parse(raw);
      return data && data.version === 1 ? normalizeItems(data.items, catalogue) : [];
    } catch (_error) { return []; }
  }

  function createStore(catalogue, storage) {
    const approved = allowedCatalogue(catalogue);
    let persistent = true;
    let items = [];
    try { items = decodeState(storage?.getItem(STORAGE_KEY), approved); }
    catch (_error) { persistent = false; }
    if (!storage) persistent = false;
    const snapshot = () => ({ items: items.map((item) => ({ ...item })), persistent });
    const persist = () => {
      try {
        if (!storage) throw new Error('storage unavailable');
        storage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, items }));
        persistent = true;
      } catch (_error) { persistent = false; }
      return snapshot();
    };
    return {
      snapshot,
      has(kind, id) {
        const canonical = normalizeItems([{ kind, id }], approved)[0];
        return Boolean(canonical && items.some((item) => item.kind === canonical.kind && item.id === canonical.id));
      },
      toggle(kind, id) {
        const canonical = normalizeItems([{ kind, id }], approved)[0];
        if (!canonical) return { ...snapshot(), error: 'unknown_item' };
        const index = items.findIndex((item) => item.kind === canonical.kind && item.id === canonical.id);
        if (index >= 0) items.splice(index, 1);
        else if (items.length >= LIMIT) return { ...snapshot(), error: 'limit_reached' };
        else items.push(canonical);
        return { ...persist(), added: index < 0 };
      },
      remove(kind, id) {
        items = items.filter((item) => item.kind !== kind || item.id !== id);
        return persist();
      },
      clear() { items = []; return persist(); },
      synchronize(raw) { items = decodeState(raw, approved); return snapshot(); }
    };
  }

  const api = { STORAGE_KEY, LIMIT, LOOKS, allowedCatalogue, resolveDesign, normalizeItems, decodeState, createStore };
  root.HEIREdit = api;
  if (typeof document === 'undefined') return;

  const config = root.HEIR || {};
  const chinese = /^zh(?:-|$)/i.test(config.locale || document.documentElement.lang);
  const fallback = {
    save: chinese ? '收藏至 THE EDIT' : 'Save to THE EDIT',
    saved: chinese ? '已收藏至 THE EDIT' : 'Saved to THE EDIT',
    added: chinese ? '已加入你的收藏。' : 'Added to your edit.',
    removed: chinese ? '已从收藏移除。' : 'Removed from your edit.',
    unavailable: chinese ? '浏览器未允许保存。本次访问内仍可收藏，关闭或刷新页面后将不保留。' : 'Browser storage is unavailable. Your edit lasts only until this page is closed or refreshed.',
    limit: chinese ? '最多可收藏 100 项。请先移除一些内容。' : 'Your edit holds up to 100 items. Remove an item before adding another.',
    error: chinese ? '暂时无法载入收藏。请刷新重试。' : 'Your edit could not load. Please refresh to try again.'
  };
  const asset = (name) => `${String(config.assetBase || '/assets/').replace(/\/+$/, '')}/${name}`;
  const path = (route) => {
    const result = `${String(config.rootUrl || '/').replace(/\/+$/, '')}/${route.replace(/^\/+/, '')}`;
    if (!config.localPreview || !chinese) return result;
    const url = new URL(result, root.location.origin);
    url.searchParams.set('lang', 'zh-CN');
    return `${url.pathname}${url.search}${url.hash}`;
  };
  const node = (tag, className, content) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (content !== undefined) element.textContent = content;
    return element;
  };
  const image = (filename, alt) => {
    const result = node('img'); result.src = asset(filename); result.alt = alt; result.loading = 'lazy';
    result.width = 700; result.height = 800; return result;
  };
  let runtime;
  let notice;
  function announce(message) {
    if (!notice) {
      notice = node('p', 'heir-edit-toast'); notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite'); document.body.append(notice);
    }
    notice.textContent = message; notice.hidden = false;
    clearTimeout(notice.__hideTimer);
    notice.__hideTimer = setTimeout(() => { notice.hidden = true; }, 7000);
  }

  function refreshControls(scope = document) {
    if (!runtime) return;
    scope.querySelectorAll('[data-heir-save-design], [data-heir-save-look]').forEach((button) => {
      const kind = button.hasAttribute('data-heir-save-design') ? 'design' : 'look';
      const id = kind === 'design' ? button.dataset.heirSaveDesign : button.dataset.heirSaveLook;
      const known = normalizeItems([{ kind, id }], runtime.catalogue).length > 0;
      const selected = known && runtime.store.has(kind, id);
      button.disabled = !known;
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-saved', selected);
      const copy = selected ? (button.dataset.savedLabel || fallback.saved) : (button.dataset.saveLabel || fallback.save);
      const label = button.querySelector('[data-heir-save-label]');
      if (label) { if (label.textContent !== copy) label.textContent = copy; }
      else if (!button.querySelector('svg') && button.textContent !== copy) button.textContent = copy;
      const product = kind === 'design' ? resolveDesign(id, runtime.catalogue) : null;
      const title = product ? (chinese ? product.titleZh || product.title : product.title) : '';
      button.setAttribute('aria-label', title ? `${copy}: ${title}` : copy);
    });
    document.querySelectorAll('[data-heir-edit-count]').forEach((element) => { element.textContent = String(runtime.store.snapshot().items.length); });
  }

  function renderPage(section) {
    if (!runtime) return;
    const strings = JSON.parse(section.querySelector('[data-edit-strings]')?.textContent || '{}');
    const state = runtime.store.snapshot();
    const filter = section.dataset.editFilter || 'design';
    const grid = section.querySelector('[data-edit-grid]');
    const empty = section.querySelector('[data-edit-empty]');
    const clear = section.querySelector('[data-edit-clear]');
    if (!grid || !empty || !clear) return;
    section.querySelectorAll('[data-edit-filter]').forEach((button) => {
      const kind = button.dataset.editFilter;
      const count = state.items.filter((entry) => entry.kind === kind).length;
      const countNode = button.querySelector('[data-edit-filter-count]');
      if (countNode) countNode.textContent = String(count);
      button.setAttribute('aria-pressed', String(kind === filter));
    });
    clear.disabled = state.items.length === 0;
    const current = state.items.filter((entry) => entry.kind === filter);
    empty.hidden = current.length > 0;
    section.querySelector('[data-edit-storage-warning]').hidden = state.persistent;
    const elements = current.map((entry) => {
      const product = entry.kind === 'design' ? resolveDesign(entry.id, runtime.catalogue) : null;
      const title = product ? (chinese ? product.titleZh || product.title : product.title)
        : strings[`${entry.id}_label`] || entry.id;
      const card = node('article', `heir-edit-card${entry.kind === 'look' ? ' heir-edit-card--look' : ''}`);
      const figure = node('div', 'heir-edit-card__image');
      figure.append(image(product ? product.image : LOOKS[entry.id].image, title));
      const remove = node('button', 'heir-edit-card__remove', strings.remove || fallback.removed);
      remove.type = 'button'; remove.dataset.editRemove = entry.id; remove.dataset.editKind = entry.kind;
      remove.setAttribute('aria-label', `${strings.remove || 'Remove'}: ${title}`);
      const body = node('div', 'heir-edit-card__body');
      body.append(node('h2', '', title), node('p', 'heir-edit-card__note', product ? strings.design_preview : strings.look_preview));
      const actions = node('div', 'heir-edit-card__actions');
      const view = node('a', '', product ? strings.view_design : strings.view_look);
      view.href = product ? path(`pages/drop-room?design=${encodeURIComponent(product.id)}`) : path(`pages/uniform#uniform-${entry.id}`);
      if (product) view.dataset.heirOpenDesign = product.id;
      actions.append(view);
      if (product) {
        const interest = node('a', '', strings.release_updates); interest.href = path(`pages/drop-room?design=${encodeURIComponent(product.id)}`);
        actions.append(interest);
      }
      body.append(actions); card.append(figure, remove, body); return card;
    });
    grid.replaceChildren(...elements);
    section.querySelector('[data-edit-loading]').hidden = true;
  }

  function changed(result, silent = false) {
    refreshControls();
    document.querySelectorAll('[data-the-edit]').forEach(renderPage);
    if (!silent) announce(result.error === 'limit_reached' ? fallback.limit : !result.persistent ? fallback.unavailable : result.added ? fallback.added : fallback.removed);
    root.dispatchEvent(new CustomEvent('heir:edit-change', { detail: { count: result.items.length, persistent: result.persistent } }));
  }

  api.refreshControls = refreshControls;
  api.path = path;
  api.asset = asset;
  api.ready = fetch(config.catalogueUrl || asset('heir-catalogue.json'), { credentials: 'omit', headers: { Accept: 'application/json' } })
    .then((response) => { if (!response.ok) throw new Error('catalogue unavailable'); return response.json(); })
    .then((payload) => {
      const catalogue = allowedCatalogue(payload);
      if (!catalogue.length) throw new Error('catalogue empty');
      let storage; try { storage = root.localStorage; } catch (_error) { storage = undefined; }
      runtime = { catalogue, store: createStore(catalogue, storage) };
      changed(runtime.store.snapshot(), true);
      return runtime;
    }).catch(() => {
      document.querySelectorAll('[data-edit-loading]').forEach((element) => { element.textContent = fallback.error; });
      document.querySelectorAll('[data-heir-save-design], [data-heir-save-look]').forEach((button) => { button.disabled = true; });
      return null;
    });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !runtime) return;
    const button = event.target.closest('[data-heir-save-design], [data-heir-save-look]');
    if (button && !button.disabled) {
      event.preventDefault();
      const kind = button.hasAttribute('data-heir-save-design') ? 'design' : 'look';
      const id = kind === 'design' ? button.dataset.heirSaveDesign : button.dataset.heirSaveLook;
      const result = runtime.store.toggle(kind, id);
      changed(result);
      if (kind === 'design' && result.added === true && !result.error) root.HEIRInsights?.track?.('save', resolveDesign(id, runtime.catalogue)?.id);
      return;
    }
    const section = event.target.closest('[data-the-edit]');
    if (!section) return;
    const filter = event.target.closest('button[data-edit-filter]');
    if (filter) { section.dataset.editFilter = filter.dataset.editFilter; renderPage(section); return; }
    const remove = event.target.closest('[data-edit-remove]');
    if (remove) {
      const result = runtime.store.remove(remove.dataset.editKind, remove.dataset.editRemove);
      changed(result); section.querySelector('[data-edit-filter][aria-pressed="true"]')?.focus(); return;
    }
    const dialog = section.querySelector('[data-edit-clear-dialog]');
    if (event.target.closest('[data-edit-clear]') && !event.target.closest('[data-edit-clear]').disabled) {
      dialog.__heirEditOpener = event.target.closest('[data-edit-clear]');
      if (dialog.dataset.editBound !== 'true') {
        dialog.dataset.editBound = 'true';
        dialog.__heirEditRestoreFocus = () => {
          const opener = dialog.__heirEditOpener;
          dialog.__heirEditOpener = null;
          if (opener?.isConnected && !opener.disabled) opener.focus();
          else section.querySelector('button[data-edit-filter][aria-pressed="true"]')?.focus();
        };
        dialog.addEventListener('close', dialog.__heirEditRestoreFocus);
        dialog.addEventListener('keydown', (keyEvent) => {
          if (keyEvent.key !== 'Escape' || typeof dialog.close === 'function') return;
          keyEvent.preventDefault(); dialog.removeAttribute('open'); dialog.hidden = true;
          dialog.__heirEditRestoreFocus();
        });
      }
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else { dialog.hidden = false; dialog.setAttribute('open', ''); }
      dialog.querySelector('[data-edit-clear-cancel]')?.focus();
    }
    if (event.target.closest('[data-edit-clear-cancel], [data-edit-clear-confirm]')) {
      if (event.target.closest('[data-edit-clear-confirm]')) changed(runtime.store.clear());
      if (typeof dialog.close === 'function') dialog.close();
      else { dialog.removeAttribute('open'); dialog.hidden = true; dialog.__heirEditRestoreFocus?.(); }
    }
  });
  root.addEventListener('storage', (event) => {
    if (!runtime || (event.key !== STORAGE_KEY && event.key !== null)) return;
    changed(runtime.store.synchronize(event.newValue), true);
  });
  const observer = new MutationObserver((mutations) => {
    if (mutations.some((entry) => [...entry.addedNodes].some((item) => item.nodeType === 1 && (item.matches?.('[data-heir-save-design], [data-heir-save-look]') || item.querySelector?.('[data-heir-save-design], [data-heir-save-look]'))))) refreshControls();
  });
  observer.observe(document.body, { childList: true, subtree: true });
})(typeof window !== 'undefined' ? window : globalThis);
